// Remote Config (app feature-flag) admin endpoints. Mirrors
// API/api/src/config/config.dto.ts and the RemoteConfig Prisma model —
// kept in sync by hand.

import { apiGet, apiPost, apiDelete, ApiError, UnauthorizedError } from './client';
import { API_BASE_URL } from '../config';
import { getToken, getRefreshToken, setSession, clearSession } from '../auth';

export interface ConfigMessage {
  id: string;
  type: string;
  title: string;
  body?: string;
  actionText?: string;
  actionURL?: string;
  startDate?: string;
  endDate?: string;
  audience?: string;
}

export interface RemoteConfigVersion {
  id: string;
  version: number;
  isActive: boolean;

  // Feature flags
  checkIDPro: boolean;
  useCheckIDVerifierToken: boolean;
  // Legacy pre-3.0 switch, retained deliberately as the permanent "off"
  // for builds already in testers' hands. Must never be true.
  useMDLDataRequestEnabled: boolean;
  // 3.0+ switch, read only once Apple has granted the requested elements.
  mdlDataRequestGrantedElementsEnabled: boolean;
  surveyURL: string | null;
  passportScanEnabled: boolean;
  mobileIDEnabled: boolean;
  scanHistoryEnabled: boolean;
  businessSubscriptionEnabled: boolean;
  teamsWebPurchaseEnabled: boolean;
  teamsWebPurchaseLinkEnabled: boolean;
  appleSandboxPurchasesOpen: boolean;
  // iPhone Duo kill switches. Missing means on.
  duoCameraDirectionEnabled: boolean;
  duoOuterDisplayEnabled: boolean;
  // iOS 27 one-split-view navigation kill switch. Missing means on.
  navContinuityEnabled: boolean;
  analyticsEnabled: boolean;
  showOnboardingEnabled: boolean;
  maxFreeDailyScans: number | null;
  maxFreeGuests: number | null;

  // Passport certificate list (CSCA)
  passportCertListVersion: string | null;
  passportCertListURL: string | null;
  passportCertListSHA256: string | null;
  passportCertListSizeBytes: number | null;
  passportCertListPublishedAt: string | null;
  passportCertListReleaseNotes: string | null;

  // Maintenance mode
  maintenanceEnabled: boolean;
  maintenanceMessage: string | null;
  maintenanceEndTime: string | null;
  maintenanceStatusURL: string | null;

  // Update gates
  minimumVersionRequired: string | null;
  minimumVersionRecommended: string | null;
  minimumVersionRequiredMessage: string | null;
  minimumVersionRecommendedMessage: string | null;
  appStoreURL: string | null;

  // Announcements
  messages: ConfigMessage[] | null;

  // Audit fields
  createdBy: string | null;
  createdByEmail: string | null;
  activatedAt: string | null;
  activatedBy: string | null;
  activatedByEmail: string | null;
  notes: string | null;

  createdAt: string;
  updatedAt: string;
}

export interface ConfigListResult {
  items: RemoteConfigVersion[];
  meta: { page: number; limit: number; total: number; pages: number };
}

/** Body accepted by POST/PUT /admin/config — matches CreateConfigDto/UpdateConfigDto. */
export interface ConfigVersionInput {
  checkIDPro?: boolean;
  useCheckIDVerifierToken?: boolean;
  useMDLDataRequestEnabled?: boolean;
  mdlDataRequestGrantedElementsEnabled?: boolean;
  surveyURL?: string | null;
  passportScanEnabled?: boolean;
  mobileIDEnabled?: boolean;
  scanHistoryEnabled?: boolean;
  businessSubscriptionEnabled?: boolean;
  teamsWebPurchaseEnabled?: boolean;
  teamsWebPurchaseLinkEnabled?: boolean;
  appleSandboxPurchasesOpen?: boolean;
  duoCameraDirectionEnabled?: boolean;
  duoOuterDisplayEnabled?: boolean;
  navContinuityEnabled?: boolean;
  analyticsEnabled?: boolean;
  showOnboardingEnabled?: boolean;
  maxFreeDailyScans?: number | null;
  maxFreeGuests?: number | null;
  passportCertListVersion?: string | null;
  passportCertListURL?: string | null;
  passportCertListSHA256?: string | null;
  passportCertListSizeBytes?: number | null;
  passportCertListPublishedAt?: string | null;
  passportCertListReleaseNotes?: string | null;
  maintenanceEnabled?: boolean;
  maintenanceMessage?: string | null;
  maintenanceEndTime?: string | null;
  maintenanceStatusURL?: string | null;
  minimumVersionRequired?: string | null;
  minimumVersionRecommended?: string | null;
  minimumVersionRequiredMessage?: string | null;
  minimumVersionRecommendedMessage?: string | null;
  appStoreURL?: string | null;
  messages?: ConfigMessage[];
  notes?: string | null;
}

/** GET /admin/config — paginated list of config versions, newest first. */
export function listConfigVersions(page = 1, limit = 20): Promise<ConfigListResult> {
  return apiGet<ConfigListResult>('/admin/config', { page, limit });
}

/** GET /admin/config/active — the currently active version, if any. */
export function getActiveConfigVersion(): Promise<RemoteConfigVersion | null> {
  return apiGet<RemoteConfigVersion | null>('/admin/config/active');
}

/** GET /admin/config/:id */
export function getConfigVersion(id: string): Promise<RemoteConfigVersion> {
  return apiGet<RemoteConfigVersion>(`/admin/config/${encodeURIComponent(id)}`);
}

/** POST /admin/config — creates a new (inactive) version. */
export function createConfigVersion(dto: ConfigVersionInput): Promise<RemoteConfigVersion> {
  return apiPost<RemoteConfigVersion>('/admin/config', dto);
}

/** POST /admin/config/:id/activate — deactivates all others. */
export function activateConfigVersion(id: string): Promise<RemoteConfigVersion> {
  return apiPost<RemoteConfigVersion>(`/admin/config/${encodeURIComponent(id)}/activate`);
}

/** POST /admin/config/:id/clone — copies a version into a new draft. */
export function cloneConfigVersion(id: string): Promise<RemoteConfigVersion> {
  return apiPost<RemoteConfigVersion>(`/admin/config/${encodeURIComponent(id)}/clone`);
}

/** DELETE /admin/config/:id — rejected by the server if the version is active. */
export function deleteConfigVersion(id: string): Promise<{ success: boolean }> {
  return apiDelete<{ success: boolean }>(`/admin/config/${encodeURIComponent(id)}`);
}

// ── PUT (update) ─────────────────────────────────────────────────
// src/api/client.ts only exposes GET/POST/PATCH/DELETE, but the config
// endpoint updates via PUT. Rather than change that shared module, this
// mirrors its request()/refresh-on-401 behavior locally.

let refreshInFlight: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/auth/v2/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
        if (!res.ok) return false;
        const pair = (await res.json()) as {
          accessToken?: string;
          refreshToken?: string;
          expiresIn?: number;
        };
        if (!pair.accessToken || !pair.refreshToken) return false;
        setSession({
          accessToken: pair.accessToken,
          refreshToken: pair.refreshToken,
          expiresIn: pair.expiresIn ?? 900,
        });
        return true;
      } catch {
        return false;
      }
    })();
  }
  const ok = await refreshInFlight;
  refreshInFlight = null;
  return ok;
}

async function apiPut<T>(path: string, body: unknown, retried = false): Promise<T> {
  const token = getToken();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });

  if (res.status === 401) {
    if (!retried && (await tryRefresh())) {
      return apiPut<T>(path, body, true);
    }
    clearSession();
    throw new UnauthorizedError();
  }

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const errBody = await res.json();
      if (errBody?.message) {
        message = Array.isArray(errBody.message)
          ? errBody.message.join(', ')
          : String(errBody.message);
      }
    } catch {
      /* non-JSON error body — keep the default message */
    }
    throw new ApiError(res.status, message);
  }

  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** PUT /admin/config/:id — updates an existing version in place. */
export function updateConfigVersion(
  id: string,
  dto: ConfigVersionInput,
): Promise<RemoteConfigVersion> {
  return apiPut<RemoteConfigVersion>(`/admin/config/${encodeURIComponent(id)}`, dto);
}
